import { useState } from 'react';

import { UserProfile, useUpdateProfile } from '../hooks/useProfile';

interface ProfileInfoCardProps {
  profile: UserProfile;
}

const authProviderLabels: Record<string, string> = {
  apple: 'Apple',
  google: 'Google',
  email: 'Email',
};

export default function ProfileInfoCard({ profile }: ProfileInfoCardProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [name, setName] = useState(profile.name || '');
  const [email, setEmail] = useState(profile.email || '');
  const updateProfile = useUpdateProfile();

  // Email can only be edited for email-based auth (not Apple/Google)
  const canEditEmail = profile.authProvider === 'email';

  const handleSaveName = async () => {
    await updateProfile.mutateAsync({ name: name.trim() || undefined });
    setIsEditingName(false);
  };

  const handleCancelName = () => {
    setName(profile.name || '');
    setIsEditingName(false);
  };

  const handleSaveEmail = async () => {
    await updateProfile.mutateAsync({ email: email.trim() });
    setIsEditingEmail(false);
  };

  const handleCancelEmail = () => {
    setEmail(profile.email || '');
    setIsEditingEmail(false);
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-purple-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        Account Info
      </h2>

      <div className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm text-purple-300 mb-1">Email</label>
          {isEditingEmail ? (
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your email"
                autoFocus
              />
              <button
                onClick={handleSaveEmail}
                disabled={updateProfile.isPending || !email.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {updateProfile.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEmail}
                disabled={updateProfile.isPending}
                className="px-4 py-2 text-purple-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-white">
                {profile.email || <span className="text-slate-400 italic">Not provided</span>}
              </span>
              {canEditEmail && (
                <button
                  onClick={() => setIsEditingEmail(true)}
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Edit
                </button>
              )}
            </div>
          )}
        </div>

        {/* Auth Provider - only show if we have a valid value */}
        {profile.authProvider && (
          <div>
            <label className="block text-sm text-purple-300 mb-1">Sign-in method</label>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600/20 text-purple-300 rounded text-sm">
              {authProviderLabels[profile.authProvider] || profile.authProvider}
            </span>
          </div>
        )}

        {/* Name (editable) */}
        <div>
          <label className="block text-sm text-purple-300 mb-1">Display name</label>
          {isEditingName ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter your name"
                autoFocus
              />
              <button
                onClick={handleSaveName}
                disabled={updateProfile.isPending}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {updateProfile.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelName}
                disabled={updateProfile.isPending}
                className="px-4 py-2 text-purple-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-white">{profile.name || 'Not set'}</span>
              <button
                onClick={() => setIsEditingName(true)}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Edit
              </button>
            </div>
          )}
        </div>

        {/* Member since */}
        <div>
          <label className="block text-sm text-purple-300 mb-1">Member since</label>
          <div className="text-white">
            {(() => {
              const createdAtDate = profile.createdAt ? new Date(profile.createdAt) : null;
              return createdAtDate && !isNaN(createdAtDate.getTime())
                ? createdAtDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'The dawn of storytelling';
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
